import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Check, Plus, X } from 'lucide-react'
import { api } from '../lib/api'
import type { Project, ProjectInvitation } from '../lib/types'
import { formatDate } from '../lib/format'
import { Link, navigate } from '../lib/router'
import { errorText, useToast } from '../components/Toaster'
import { Badge, Button, Field, Input, Modal, Notice, Spinner, Textarea } from '../ds'

export function Dashboard() {
  const { notify } = useToast()
  const [projects, setProjects] = useState<Project[] | null>(null)
  const [invitations, setInvitations] = useState<ProjectInvitation[] | null>(null)
  const [busyInvitationId, setBusyInvitationId] = useState<string | null>(null)
  const [loadError, setLoadError] = useState('')
  const [showCreate, setShowCreate] = useState(false)

  function load() {
    setLoadError('')
    Promise.all([api.listProjects(), api.listProjectInvitations()])
      .then(([projectsRes, invitationsRes]) => {
        setProjects(projectsRes.projects)
        setInvitations(invitationsRes.invitations)
      })
      .catch((err) => setLoadError(errorText(err)))
  }

  useEffect(load, [])

  async function acceptInvitation(invitation: ProjectInvitation) {
    const projectId = invitation.project.id
    if (!projectId) return
    setBusyInvitationId(projectId)
    try {
      const res = await api.acceptProjectInvite(projectId)
      notify(`Проект «${res.project.name}» добавлен`)
      load()
      navigate(`/projects/${projectId}`)
    } catch (err) {
      notify(errorText(err), 'error')
    } finally {
      setBusyInvitationId(null)
    }
  }

  async function declineInvitation(invitation: ProjectInvitation) {
    const projectId = invitation.project.id
    if (!projectId) return
    setBusyInvitationId(projectId)
    try {
      await api.declineProjectInvite(projectId)
      notify(`Приглашение в «${invitation.project.name}» отклонено`)
      load()
    } catch (err) {
      notify(errorText(err), 'error')
    } finally {
      setBusyInvitationId(null)
    }
  }

  const isLoaded = projects !== null && invitations !== null
  const hasProjects = Boolean(projects?.length)
  const hasInvitations = Boolean(invitations?.length)

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Проекты</h1>
          <p className="page-sub">Создавайте проекты и отслеживайте упоминания в соцсетях.</p>
        </div>
        <div className="page-actions">
          <Button onClick={() => setShowCreate(true)}>
            <Plus size={16} /> Новый проект
          </Button>
        </div>
      </div>

      {loadError && <Notice>{loadError}</Notice>}

      {!isLoaded && !loadError && (
        <div className="loading-screen">
          <Spinner />
        </div>
      )}

      {invitations && invitations.length > 0 && (
        <div className="invite-list">
          {invitations.map((invitation) => (
            <div key={invitation.project.id || invitation.project.slug} className="invite-card">
              <div>
                <div className="invite-title">{`Вас пригласили в проект «${invitation.project.name}»`}</div>
                <div className="tile-meta">
                  {invitation.invitedByEmail ? `Пригласил ${invitation.invitedByEmail}` : 'Приглашение в проект'}
                </div>
              </div>
              <div className="row">
                <Button
                  size="sm"
                  disabled={busyInvitationId === invitation.project.id}
                  onClick={() => acceptInvitation(invitation)}
                >
                  <Check size={14} /> Принять
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busyInvitationId === invitation.project.id}
                  onClick={() => declineInvitation(invitation)}
                >
                  <X size={14} /> Отклонить
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isLoaded && !hasProjects && !hasInvitations && (
        <div className="empty">Пока нет проектов. Создайте первый — кнопка справа сверху.</div>
      )}

      {projects && projects.length > 0 && (
        <div className="tiles">
          {projects.map((project) => (
            <Link key={project.id} href={`/projects/${project.id}`} className="tile">
              <div>
                <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                  <div className="tile-name">{project.name}</div>
                  <Badge tone={project.membershipRole === 'owner' ? 'active' : 'invited'}>
                    {project.membershipRole === 'owner' ? 'Владелец' : 'Приглашённый'}
                  </Badge>
                </div>
                <div className="tile-meta">обновлён {formatDate(project.updatedAt)}</div>
              </div>
              <ProjectStats project={project} />
            </Link>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreated={(project) => {
            setShowCreate(false)
            notify(`Проект «${project.name}» создан`)
            load()
          }}
        />
      )}
    </div>
  )
}

function ProjectStats({ project }: { project: Project }) {
  const counters = project.counters
  if (!counters) {
    return null
  }
  return (
    <div className="tile-stats">
      <span className="tile-stat">{counters.keywordCount} ключей</span>
      <span className="tile-stat">{counters.postCount} постов</span>
      {counters.newCount > 0 && (
        <span className="tile-stat tone-accent">
          <span className="dot" />
          {counters.newCount} новых
        </span>
      )}
    </div>
  )
}

function CreateProjectModal({
  onClose,
  onCreated
}: {
  onClose: () => void
  onCreated: (project: Project) => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!name.trim()) {
      return
    }
    setSaving(true)
    setError('')
    try {
      const res = await api.createProject({ name: name.trim(), description: description.trim() })
      onCreated(res.project)
    } catch (err) {
      setError(errorText(err))
      setSaving(false)
    }
  }

  return (
    <Modal title="Новый проект" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        {error && <Notice>{error}</Notice>}
        <Field label="Название">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="Напр. Бренд X" />
        </Field>
        <Field label="Описание проекта" hint="Коротко о чём проект — ИИ учитывает это при генерации. Необязательно.">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Напр. приложение для подсчёта КБЖУ по фото…"
          />
        </Field>
        <div className="row spread">
          <span />
          <div className="row">
            <Button type="button" variant="secondary" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? 'Создаём…' : 'Создать'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
